import React, { useState, useEffect, useRef } from 'react';
import { Button, Paper } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { ConnectedPlainInput } from 'src/components/ConnectedInputs';
import { mdiSendVariantOutline } from '@mdi/js';
import { Icon } from '@mdi/react';
import formStyles from '../Loan/form.module.scss';
import innerStyles from './index.module.scss';
import clsx from 'clsx';
import ChatMessageBox from './ChatMessageBox';
import { sendChatQuery } from 'src/actions';
import NqmGuidelineInstructions from './Instructions/NqmGuidelineInstructions';
import PipelineInstructions from './Instructions/PipelineInstructions';

const styles = {
  ...formStyles,
  ...innerStyles,
};

/**
 * @param {"guideline" | "pipeline"} chatType
 */
const AiChat = ({ chatType }) => {
  const containerRef = useRef(null);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      question: '',
    },
  });

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (chatType) {
      setMessages([]);
      setSessionId('');
      formMethods.setValue('question', '');
    }
  }, [chatType, formMethods]);

  const onSubmit = async (data) => {
    if (data.question.trim().toLowerCase() === 'clear') {
      setMessages([]);
      setSessionId('');
      formMethods.setValue('question', '');
      return;
    }
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        loading: false,
        text: data.question,
        role: 'user',
      },
      {
        loading: true,
        role: 'assistant',
      },
    ]);

    let question = data.question;
    formMethods.setValue('question', '');
    let result = {};
    try {
      result = await sendChatQuery(chatType, sessionId, question);
      setSessionId(result?.sessionId);
    } catch (e) {
      result = { reponse: `Could not send message: ${e.message}` };
    }

    setMessages((prevMessages) => {
      const newItems = [...prevMessages];
      newItems[newItems.length - 1] = {
        loading: false,
        text: result.response,
      };
      return newItems;
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      formMethods.handleSubmit(onSubmit)();
    }
  };

  const inputLabel =
    messages.length === 0
      ? 'Enter your question here'
      : "Enter your question here (or 'clear' to start a new session)";

  const instructions =
    chatType === 'guideline' ? (
      <NqmGuidelineInstructions />
    ) : chatType === 'pipeline' ? (
      <PipelineInstructions />
    ) : (
      <>Unknown Chat</>
    );

  return (
    <Paper
      className={clsx(styles.form)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        marginBottom: '0px',
      }}
    >
      {messages.length !== 0 && (
        <div className={styles.disclaimer}>
          GPT can make mistakes. Check important info. This chat has access to{' '}
          {chatType} data.
        </div>
      )}
      <ChatMessageBox messages={messages} instructions={instructions} />
      <FormProvider {...formMethods}>
        <div className={styles.inputContainer}>
          <ConnectedPlainInput
            required
            name={'question'}
            onKeyDown={handleKeyDown}
            label={inputLabel}
            className={styles.input}
          />
          <Button
            color={'primary'}
            variant={'contained'}
            disabled={!formMethods.formState.isValid}
            onClick={formMethods.handleSubmit(onSubmit)}
            startIcon={<Icon path={mdiSendVariantOutline} />}
          >
            Send
          </Button>
        </div>
      </FormProvider>
    </Paper>
  );
};

export default AiChat;
